import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import CampaignIcon from '@mui/icons-material/Campaign';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FilterListIcon from '@mui/icons-material/FilterList';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import TaskAltIcon from '@mui/icons-material/TaskAlt';

import AuthContext from '../../context/authContext';
import useIssue from '../../hooks/useIssue';
import useBranch from '../../hooks/useBranch';
import IssueDetailDialog from '../Issues/IssueDetailDialog';
import IssuePriorityChip from '../Issues/IssuePriorityChip';
import { subscribeCountsInvalidate } from '../../util/countInvalidationBus';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import {
  ISSUE_PRIORITIES,
  ISSUE_PRIORITY_BY_KEY,
  ISSUE_STATUS_BY_KEY,
  KANBAN_STATUSES,
} from '../../util/issueConstants';

// Issues older than this stay hidden in the resolved column unless the
// "Erledigte anzeigen" toggle is on.
const CLOSED_VISIBLE_DAYS = 7;

const PRIORITY_RANK = { p1: 0, p2: 1, p3: 2, p4: 3, p5: 4 };

// Compact German age label for a card ("vor 5 Min", "vor 3 Std", …).
const formatAge = value => {
  if (!value) return '';
  const minutes = dayjs().diff(dayjs(value), 'minute');
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `vor ${minutes} Min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `vor ${hours} Std`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `vor ${days} T`;
  return dayjs(value).format('DD.MM.YYYY HH:mm');
};

// Column an issue is rendered in; cancelled issues share the resolved column.
const columnOfIssue = issue => (KANBAN_STATUSES.includes(issue.status) ? issue.status : 'resolved');

function DraggableIssueCard({ issue, disabled, renderCard }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({
    id: issue._id,
    disabled,
    data: { issue },
  });
  const dragHandle = disabled ? null : (
    <Tooltip title='Status per Drag & Drop ändern'>
      <Box
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        onClick={event => event.stopPropagation()}
        aria-label='Status per Drag & Drop ändern'
        sx={{
          touchAction: 'none',
          cursor: isDragging ? 'grabbing' : 'grab',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 32,
          minHeight: 32,
          mr: -0.75,
          my: -0.5,
          borderRadius: 1,
          color: 'text.disabled',
          '&:hover': { color: 'text.secondary', bgcolor: 'action.hover' },
          '&:focus-visible': {
            color: 'text.secondary',
            bgcolor: 'action.hover',
            outline: '2px solid',
            outlineColor: 'primary.main',
          },
        }}
      >
        <DragIndicatorIcon fontSize='small' />
      </Box>
    </Tooltip>
  );

  return (
    <div ref={setNodeRef} style={{ opacity: isDragging ? 0.25 : 1 }}>
      {renderCard(issue, isDragging, dragHandle)}
    </div>
  );
}

function KanbanColumn({ status, count, dragActive, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: status,
    data: { status },
  });
  const config = ISSUE_STATUS_BY_KEY[status];
  return (
    <Paper
      ref={setNodeRef}
      variant='outlined'
      sx={{
        flex: { xs: '0 0 auto', md: '1 1 0' },
        minWidth: { xs: 0, md: 300 },
        width: { xs: '100%', md: 'auto' },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        bgcolor: alpha(config.color, isOver ? 0.12 : 0.05),
        borderColor: isOver ? config.color : 'divider',
        boxShadow: isOver ? `inset 0 0 0 1px ${config.color}` : 'none',
        transition: 'background-color 0.15s, border-color 0.15s, box-shadow 0.15s',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1 }}>
        <Typography
          variant='subtitle2'
          sx={{ fontWeight: 700, color: config.color, letterSpacing: 0.3 }}
        >
          {config.label}
        </Typography>
        <Chip
          size='small'
          label={count}
          sx={{ bgcolor: alpha(config.color, 0.15), color: config.color, fontWeight: 600 }}
        />
        {dragActive && isOver && (
          <Typography variant='caption' sx={{ ml: 'auto', color: config.color, fontWeight: 700 }}>
            Hier ablegen
          </Typography>
        )}
      </Box>
      <Stack spacing={1} sx={{ px: 1, pb: 1.5, flex: 1, minHeight: { xs: 56, md: 120 } }}>
        {children}
      </Stack>
    </Paper>
  );
}

/**
 * "Supportsicht" kanban board for issues ("Mängel & Störungen"): filterable
 * four-column board with drag & drop status changes and a detail dialog.
 */
export default function IssueBoardView() {
  const theme = useTheme();
  // Below md the board stacks its columns vertically and the filter toolbar
  // collapses behind a filter button.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authCtx = useContext(AuthContext);
  const { getIssues, changeIssueStatus } = useIssue();
  const { getBranches } = useBranch();

  const canRead = authCtx.checkRight('issue:read') || authCtx.checkRight('issue:read:own');
  const canManage = authCtx.checkRight('issue:manage');
  const canCreate = authCtx.checkRight('issue:create');

  const [issues, setIssues] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const initialLoaded = useRef(false);

  // Filter state (client-side filtering over the loaded list).
  const [search, setSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [onlyMine, setOnlyMine] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const searchRef = useRef(null);
  const searchTimer = useRef(null);

  const [detailIssueId, setDetailIssueId] = useState(null);
  const [activeIssueId, setActiveIssueId] = useState(null);
  const [statusChangePending, setStatusChangePending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getIssues({ includeClosed: 'true' });
      setIssues(result?.issues || []);
    } catch {
      // Error toast is handled inside useIssue.
    } finally {
      initialLoaded.current = true;
      setLoading(false);
    }
  }, [getIssues]);

  // Initial load: issues plus filter reference data, and the ?issueId= deep link.
  useEffect(() => {
    if (!canRead) return;
    load();
    getBranches()
      .then(result => setBranches(Array.isArray(result) ? result : result?.branches || []))
      .catch(() => {});
    const deepLinkId = searchParams.get('issueId');
    if (deepLinkId) setDetailIssueId(deepLinkId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  // Live refresh: piggyback on the Socket.io counts-invalidation bus with a
  // small debounce so bursts of events cause a single refetch.
  useEffect(() => {
    if (!canRead) return undefined;
    let timer = null;
    const unsubscribe = subscribeCountsInvalidate(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => load(), 1500);
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [canRead, load]);

  // Free-text search: uncontrolled input, applied with a small debounce.
  const applySearch = useCallback(() => {
    setSearch(searchRef.current?.value?.trim().toLowerCase() || '');
  }, []);

  const handleSearchChange = useCallback(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(applySearch, 300);
  }, [applySearch]);

  useEffect(() => {
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, []);

  const filteredIssues = useMemo(() => {
    const now = dayjs();
    const me = authCtx.currentUser;
    return issues.filter(issue => {
      const isClosed = issue.status === 'resolved' || issue.status === 'cancelled';
      if (isClosed && !showClosed) {
        const closedAt = issue.resolvedAt || issue.meta?.createdAt;
        if (!closedAt || now.diff(dayjs(closedAt), 'day') >= CLOSED_VISIBLE_DAYS) return false;
      }
      if (branchFilter && issue.branchId?._id !== branchFilter) return false;
      if (priorityFilter.length > 0 && !priorityFilter.includes(issue.priority)) return false;
      if (onlyMine) {
        const assignedId = issue.assignedUserId?._id || issue.assignedUserId;
        const isMine =
          (me?.userId && assignedId === me.userId) ||
          (me?.userName && issue.assignedUsername === me.userName);
        if (!isMine) return false;
      }
      if (search) {
        const haystack = [
          issue.title,
          issue.description,
          issue.branchId?.longName,
          issue.branchId?.locationName,
          issue.entertainmentId?.longName,
          issue.entertainmentId?.entertainmentName,
          issue.resourceId?.name,
          issue.assignedUsername,
          issue.reportedByUsername,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [issues, showClosed, branchFilter, priorityFilter, onlyMine, search, authCtx]);

  // Group by kanban column and sort by priority, newest first within a level.
  const columns = useMemo(() => {
    const byColumn = Object.fromEntries(KANBAN_STATUSES.map(status => [status, []]));
    filteredIssues.forEach(issue => byColumn[columnOfIssue(issue)].push(issue));
    KANBAN_STATUSES.forEach(status =>
      byColumn[status].sort((a, b) => {
        const p = (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9);
        if (p) return p;
        const at = a.meta?.createdAt ? new Date(a.meta.createdAt).getTime() : 0;
        const bt = b.meta?.createdAt ? new Date(b.meta.createdAt).getTime() : 0;
        return bt - at;
      })
    );
    return byColumn;
  }, [filteredIssues]);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
  );

  const handleDragStart = useCallback(({ active }) => {
    setActiveIssueId(active.id);
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveIssueId(null);
  }, []);

  const handleDragEnd = useCallback(
    async ({ active, over }) => {
      setActiveIssueId(null);
      if (!over || statusChangePending) return;
      const dragged = issues.find(issue => issue._id === active.id);
      if (!dragged) return;
      const targetStatus = over.data.current?.status;
      if (!targetStatus || targetStatus === columnOfIssue(dragged)) return;
      const previousStatus = dragged.status;
      const previousResolvedAt = dragged.resolvedAt;
      const optimisticResolvedAt =
        targetStatus === 'resolved' ? new Date().toISOString() : undefined;
      setIssues(prev =>
        prev.map(issue =>
          issue._id === dragged._id
            ? { ...issue, status: targetStatus, resolvedAt: optimisticResolvedAt }
            : issue
        )
      );
      setStatusChangePending(true);
      try {
        const result = await changeIssueStatus(dragged._id, targetStatus);
        if (result?.issue) {
          setIssues(prev => prev.map(issue => (issue._id === dragged._id ? result.issue : issue)));
        }
        toast.success(`Status geändert: ${ISSUE_STATUS_BY_KEY[targetStatus].label}`);
      } catch {
        setIssues(prev =>
          prev.map(issue =>
            issue._id === dragged._id
              ? { ...issue, status: previousStatus, resolvedAt: previousResolvedAt }
              : issue
          )
        );
      } finally {
        setStatusChangePending(false);
      }
    },
    [issues, changeIssueStatus, statusChangePending]
  );

  const renderCard = useCallback(
    (issue, isDragging = false, dragHandle = null, isOverlay = false) => {
      const priorityColor = ISSUE_PRIORITY_BY_KEY[issue.priority]?.color || theme.palette.divider;
      const locationLine = [
        issue.entertainmentId?.longName || issue.entertainmentId?.entertainmentName,
        issue.resourceId?.name,
      ]
        .filter(Boolean)
        .join(' / ');
      const branchName = issue.branchId?.longName || issue.branchId?.locationName;
      const assignee = issue.assignedUserId?.username || issue.assignedUsername || null;
      const isUrgent = issue.priority === 'p1' && issue.status === 'open';
      // Cards in "Erledigt" (cancelled shares the column) read as done, same as
      // on the developer board: dimmed card, struck-through title.
      const isDone = issue.status === 'resolved' || issue.status === 'cancelled';
      return (
        <Paper
          variant='outlined'
          onClick={isOverlay ? undefined : () => setDetailIssueId(issue._id)}
          sx={{
            p: 1.25,
            borderRadius: 2,
            borderLeft: '4px solid',
            borderLeftColor: priorityColor,
            cursor: 'pointer',
            transition: 'box-shadow 0.15s',
            ...(isDone && { opacity: 0.7 }),
            ...(isOverlay && { cursor: 'grabbing', pointerEvents: 'none' }),
            '&:hover': { boxShadow: 2 },
            ...(isDragging && { boxShadow: 6 }),
            ...(isUrgent &&
              !isDragging && {
                '@keyframes issueP1Pulse': {
                  '0%': { boxShadow: `0 0 0 0 ${alpha(priorityColor, 0.4)}` },
                  '70%': { boxShadow: `0 0 0 7px ${alpha(priorityColor, 0)}` },
                  '100%': { boxShadow: `0 0 0 0 ${alpha(priorityColor, 0)}` },
                },
                animation: 'issueP1Pulse 2s ease-out infinite',
              }),
          }}
        >
          <Stack direction='row' spacing={0.75} sx={{ alignItems: 'center' }}>
            <IssuePriorityChip priority={issue.priority} />
            <Box sx={{ flex: 1 }} />
            {(issue.photos || []).length > 0 && (
              <Stack direction='row' spacing={0.25} sx={{ alignItems: 'center' }}>
                <PhotoCameraOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                <Typography variant='caption' color='text.secondary'>
                  {issue.photos.length}
                </Typography>
              </Stack>
            )}
            <Typography variant='caption' color='text.disabled' sx={{ whiteSpace: 'nowrap' }}>
              {formatAge(issue.meta?.createdAt)}
            </Typography>
            {dragHandle}
          </Stack>
          <Typography
            variant='subtitle2'
            sx={{
              fontWeight: 600,
              mt: 0.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              overflowWrap: 'anywhere',
              ...(isDone && { textDecoration: 'line-through' }),
            }}
          >
            {issue.title}
          </Typography>
          {(locationLine || branchName) && (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block', mt: 0.25 }}
              noWrap
            >
              {[branchName, locationLine].filter(Boolean).join(' · ')}
            </Typography>
          )}
          {assignee && (
            <Stack direction='row' spacing={0.75} sx={{ alignItems: 'center', mt: 0.75 }}>
              <Avatar sx={{ width: 20, height: 20, fontSize: '0.65rem' }}>
                {assignee.slice(0, 2).toUpperCase()}
              </Avatar>
              <Typography variant='caption' color='text.secondary' noWrap>
                {assignee}
              </Typography>
            </Stack>
          )}
        </Paper>
      );
    },
    [theme]
  );

  const showSkeletons = loading && !initialLoaded.current;
  const activeIssue = activeIssueId
    ? issues.find(issue => issue._id === activeIssueId) || null
    : null;

  const activeFilterCount =
    (branchFilter ? 1 : 0) +
    (priorityFilter.length > 0 ? 1 : 0) +
    (onlyMine ? 1 : 0) +
    (showClosed ? 1 : 0);

  // Shared filter controls, rendered inline on desktop and inside the
  // collapsible section on mobile (full width there).
  const filterControls = (
    <>
      <FormControl size='small' sx={{ minWidth: 160, width: { xs: '100%', md: 'auto' } }}>
        <InputLabel id='issue-branch-filter-label'>Standort</InputLabel>
        <Select
          labelId='issue-branch-filter-label'
          label='Standort'
          value={branchFilter}
          onChange={event => setBranchFilter(event.target.value)}
        >
          <MenuItem value=''>Alle Standorte</MenuItem>
          {branches.map(branch => (
            <MenuItem key={branch._id} value={branch._id}>
              {branch.longName || branch.locationName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size='small' sx={{ minWidth: 150, width: { xs: '100%', md: 'auto' } }}>
        <InputLabel id='issue-priority-filter-label'>Priorität</InputLabel>
        <Select
          labelId='issue-priority-filter-label'
          label='Priorität'
          multiple
          value={priorityFilter}
          onChange={event => {
            const value = event.target.value;
            setPriorityFilter(typeof value === 'string' ? value.split(',') : value);
          }}
          renderValue={selected => (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {selected.map(key => (
                <IssuePriorityChip key={key} priority={key} />
              ))}
            </Box>
          )}
        >
          {ISSUE_PRIORITIES.map(priority => (
            <MenuItem key={priority.key} value={priority.key}>
              {priority.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControlLabel
        control={
          <Switch
            size='small'
            checked={onlyMine}
            onChange={event => setOnlyMine(event.target.checked)}
          />
        }
        label={<Typography variant='body2'>Nur mir zugewiesen</Typography>}
      />
      <FormControlLabel
        control={
          <Switch
            size='small'
            checked={showClosed}
            onChange={event => setShowClosed(event.target.checked)}
          />
        }
        label={<Typography variant='body2'>Erledigte anzeigen</Typography>}
      />
    </>
  );

  if (!canRead) return <Navigate to='/' replace />;

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: { xs: 2, md: 3 } }}>
      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant='h5' sx={{ lineHeight: 1.3 }}>
          Mängel &amp; Störungen
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            ml: 'auto',
            flexWrap: 'wrap',
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {canManage && (
            <Button
              variant='outlined'
              size={isMobile ? 'small' : 'medium'}
              startIcon={<CampaignIcon />}
              onClick={() => navigate('/issues/escalations')}
              sx={{ whiteSpace: 'nowrap', flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
            >
              Eskalations-Audit
            </Button>
          )}
          {canCreate && (
            <Button
              variant='contained'
              size={isMobile ? 'small' : 'medium'}
              startIcon={<AddIcon />}
              onClick={() => navigate('/issues/report')}
              sx={{ whiteSpace: 'nowrap', flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
            >
              Issue melden
            </Button>
          )}
        </Box>
      </Box>

      {/* Filter toolbar: one wrap row on desktop; on mobile a compact row
          (search, filter toggle, refresh) with the filters collapsed below. */}
      <Paper
        variant='outlined'
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar - 1,
          p: 1.5,
          mb: 2,
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
          <TextField
            inputRef={searchRef}
            size='small'
            placeholder='Suchen…'
            onChange={handleSearchChange}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                if (searchTimer.current) clearTimeout(searchTimer.current);
                applySearch();
              }
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon fontSize='small' sx={{ color: 'text.disabled', mr: 0.5 }} />
              ),
            }}
            sx={{ minWidth: 0, flex: '1 1 160px', maxWidth: { md: 320 } }}
          />
          {isMobile ? (
            <Tooltip title='Filter'>
              <IconButton
                size='small'
                onClick={() => setFiltersOpen(open => !open)}
                color={filtersOpen ? 'primary' : 'default'}
                aria-label='Filter ein-/ausblenden'
              >
                <Badge badgeContent={activeFilterCount} color='primary'>
                  <FilterListIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          ) : (
            filterControls
          )}
          <Tooltip title='Aktualisieren'>
            <span style={{ marginLeft: isMobile ? 0 : 'auto' }}>
              <IconButton size='small' onClick={load} disabled={loading}>
                {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {isMobile && (
          <Collapse in={filtersOpen}>
            <Stack spacing={1.5} sx={{ pt: 1.5 }}>
              {filterControls}
            </Stack>
          </Collapse>
        )}
      </Paper>

      {/* Kanban board */}
      {showSkeletons ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 1.5,
            overflowX: { md: 'auto' },
            pb: 1,
          }}
        >
          {KANBAN_STATUSES.map(status => (
            <Skeleton
              key={status}
              variant='rounded'
              height={isMobile ? 140 : 320}
              sx={{ flex: { md: '1 1 0' }, minWidth: { md: 300 }, borderRadius: 2 }}
            />
          ))}
        </Box>
      ) : (
        <DndContext
          sensors={dndSensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 1.5,
              overflowX: { md: 'auto' },
              alignItems: 'stretch',
              pb: 1,
            }}
          >
            {KANBAN_STATUSES.map(status => (
              <KanbanColumn
                key={status}
                status={status}
                count={columns[status].length}
                dragActive={Boolean(activeIssue)}
              >
                {columns[status].length === 0 ? (
                  <Box sx={{ py: 3, textAlign: 'center' }}>
                    <TaskAltIcon sx={{ fontSize: 26, color: 'text.disabled' }} />
                    <Typography variant='caption' color='text.disabled' sx={{ display: 'block' }}>
                      Keine Issues
                    </Typography>
                  </Box>
                ) : (
                  columns[status].map(issue => (
                    <DraggableIssueCard
                      key={issue._id}
                      issue={issue}
                      disabled={!canManage || statusChangePending}
                      renderCard={renderCard}
                    />
                  ))
                )}
              </KanbanColumn>
            ))}
          </Box>
          <DragOverlay dropAnimation={null}>
            {activeIssue ? renderCard(activeIssue, true, null, true) : null}
          </DragOverlay>
        </DndContext>
      )}

      <IssueDetailDialog
        open={Boolean(detailIssueId)}
        issueId={detailIssueId}
        onClose={() => setDetailIssueId(null)}
        onChanged={load}
      />
    </Box>
  );
}
